import * as React from 'react';
import { useState } from 'react';

import classNames from 'classnames';

import { Button, VisuallyHidden } from '@coursera/cds-core';
import { SpinnerIcon } from '@coursera/cds-icons';

import type { ApiError } from 'bundles/enroll-common/utils/errorUtils';
import { useEnrollInS12nSubscription } from 'bundles/enroll-common/utils/useEnrollInS12nSubscription';
import Icon from 'bundles/iconfont/Icon';
import TrackedButton from 'bundles/page/components/TrackedButton';
import { getTrackingData } from 'bundles/promotions/utils/productDiscountPromoUtils';

import _t from 'i18n!nls/enroll';

import 'css!./__styles__/SubscriptionEnrollButton';

type Props = {
  isFromS12nSelection: boolean;
  onError: (error: ApiError) => void;
  showCds?: boolean;
};

const SubscriptionEnrollButton = ({ isFromS12nSelection, onError, showCds = false }: Props) => {
  const [enrolling, setEnrolling] = useState(false);
  const { enrollInSubscription, canEnrollThroughS12nSubscriptionFreeTrial: isFreeTrial } =
    useEnrollInS12nSubscription(isFromS12nSelection);

  const handleError = (error: ApiError) => {
    setEnrolling(false);
    onError(error);
  };

  const handleClick = async () => {
    setEnrolling(true);
    enrollInSubscription().catch(handleError);
  };

  const renderButtonContent = () => {
    if (enrolling && !showCds) {
      // return
      return '';
    } else {
      return isFreeTrial ? _t('Start Free Trial') : _t('Subscribe');
    }
  };

  const trackingName = isFreeTrial ? 'free_trial_start_button' : 'enroll_subscribe_monthly';

  const buttonClassName = !showCds
    ? classNames('subscribe-button', {
        'primary cozy': !enrolling,
      })
    : undefined;

  const getButtonIcon = () => {
    if (enrolling) {
      return showCds ? <SpinnerIcon /> : <Icon name="spinner" spin />;
    }
    return undefined;
  };

  return (
    <div className="rc-SubscriptionEnrollButton" role="status" aria-live="polite">
      <Button
        component={TrackedButton}
        data-e2e="subscription_enroll_button"
        trackingName={trackingName}
        data={getTrackingData()}
        className={buttonClassName}
        onClick={handleClick}
        disabled={enrolling}
        withVisibilityTracking={false}
        requireFullyVisible={false}
        icon={getButtonIcon()}
        variant={showCds ? 'primary' : undefined}
        aria-live="polite"
      >
        {renderButtonContent()}
        {enrolling && (
          <VisuallyHidden component="span" className="rc-A11yScreenReaderOnly">
            {_t('Loading')}
          </VisuallyHidden>
        )}
      </Button>
    </div>
  );
};

export default SubscriptionEnrollButton;
